import { useMemo } from 'react';
import { useTheme } from '@material-ui/styles';
import { DateTime } from 'luxon';
import { PERIOD_SCALE_ENUM } from '~/modules/resourcing/common/enums';
import { weekUris } from '~/modules/common/dates/dayOfWeek';
import { useMeContext } from '~/modules/me/useMeContext';
import useThemeBreakpoint from '~/modules/common/hooks/useThemeBreakpoint';
import { getBenchTimelinePeriodCount } from '../config/benchTimelineChartConfig';

const LUXON_WEEKDAY_OFFSET = 1;

const alignToWorkWeek = (date, workWeekStartDayUri) => {
  const targetWeekday =
    weekUris.indexOf(workWeekStartDayUri) + LUXON_WEEKDAY_OFFSET;
  const alignedDate = date.set({
    weekday: targetWeekday
  });

  return alignedDate > date ? alignedDate.minus({ week: 1 }) : alignedDate;
};

export const getDateRange = ({
  startDate,
  periodScale,
  workWeekStartDayUri,
  periodCount
}) => {
  if (periodScale === PERIOD_SCALE_ENUM.WEEKS) {
    const alignedDate = alignToWorkWeek(startDate, workWeekStartDayUri);
    const rangeStartDate = alignedDate.minus({ weeks: 1 });
    const rangeEndDate = rangeStartDate.plus({ weeks: periodCount - 1 });

    return { startDate: rangeStartDate, endDate: rangeEndDate };
  }

  const rangeStartDate = startDate
    .startOf(periodScale)
    .minus({ [periodScale]: 1 });

  const rangeEndDate =
    periodScale === PERIOD_SCALE_ENUM.DAYS
      ? rangeStartDate.plus({ [periodScale]: periodCount - 1 })
      : rangeStartDate
          .plus({ [periodScale]: periodCount - 1 })
          .endOf(periodScale);

  return { startDate: rangeStartDate, endDate: rangeEndDate };
};

export const useBenchTimelineDateRange = ({
  startDate = DateTime.local().startOf('month'),
  periodScale = PERIOD_SCALE_ENUM.MONTHS
}) => {
  const theme = useTheme();
  const breakPoint = useThemeBreakpoint(theme);
  const {
    workWeekStartDay: { uri: workWeekStartDayUri }
  } = useMeContext();

  const dateRange = useMemo(() => {
    const periodCount = getBenchTimelinePeriodCount(breakPoint);

    return getDateRange({
      startDate,
      periodScale,
      workWeekStartDayUri,
      periodCount
    });
  }, [startDate, periodScale, workWeekStartDayUri, breakPoint]);

  return { dateRange };
};

export default useBenchTimelineDateRange;
